/*
  The Audio Tailor: shared theme JavaScript (loaded on every page as a module).
  Keep this file small. Feature code lives in its own asset, loaded only where needed.

  Exposes window.TAT helpers used by feature scripts:
    TAT.fetchSection(url, sectionId)   -> Promise<string> (Section Rendering API)
    TAT.onVisible(el, callback, margin) -> runs once when el nears the viewport
    TAT.loadScript(src)                 -> Promise, loads a script once
    TAT.formatMoney(cents)              -> string using the shop money format
    TAT.resolveSearch(query)            -> Promise<string>, the form of a model number search that finds it
    TAT.normaliseQuery(query)           -> best guess form of a model number search, without asking Shopify
    TAT.cart.get() / add(body) / change(body) -> Promise<cart>, dispatches 'cart:updated'
  Custom elements defined here: <tat-drawer>, <tat-scroll-row>
*/

const TAT = (window.TAT = window.TAT || {});

TAT.fetchSection = async (url, sectionId) => {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('section_id', sectionId);
  const res = await fetch(u.toString(), { headers: { Accept: 'text/html' } });
  if (!res.ok) throw new Error(`Section ${sectionId} failed: ${res.status}`);
  return res.text();
};

TAT.onVisible = (el, callback, margin = '400px') => {
  if (!el) return;
  if (!('IntersectionObserver' in window)) {
    callback();
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        callback();
      }
    },
    { rootMargin: margin }
  );
  io.observe(el);
};

const loadedScripts = new Map();
TAT.loadScript = (src, attrs = {}) => {
  if (loadedScripts.has(src)) return loadedScripts.get(src);
  const p = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  loadedScripts.set(src, p);
  return p;
};

// Supports every Shopify money placeholder; no-decimal formats round (like Liquid's money filter).
TAT.formatMoney = (cents, format = TAT.settings?.moneyFormat || '${{amount}}') => {
  const amount = Number(cents) || 0;
  const group = (decimals, thousands, decimal) => {
    const [whole, dec] = (amount / 100).toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return dec ? `${grouped}${decimal}${dec}` : grouped;
  };
  return String(format).replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
    switch (name) {
      case 'amount': return group(2, ',', '.');
      case 'amount_no_decimals': return group(0, ',', '.');
      case 'amount_with_comma_separator': return group(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return group(0, '.', ',');
      case 'amount_with_space_separator': return group(2, ' ', ',');
      case 'amount_no_decimals_with_space_separator': return group(0, ' ', ',');
      case 'amount_with_apostrophe_separator': return group(2, "'", '.');
      default: return match;
    }
  });
};

// Model numbers: Shopify indexes "HT/1003" as "ht" + "1003", "PM6007" as one word and "X-P700" as "x" + "p700", and
// people type each of them every way ("ht1003", "ht/1003", "ht 1003"). TAT.searchCandidates lists the plain queries
// to try, best guess first: as typed, split between letters and digits, spaced at the slash or hyphen, joined, then
// (one model number only) split at every single point for brands that hyphenate in odd places ("dp-ub9000").
// TAT.resolveSearch tries them in order and keeps the first whose results contain the model number (ignoring spaces,
// slashes and hyphens). Shopify's OR syntax is not used: it is unreliable with several alternatives.
// Model numbers typed with spaces ("ht 1003", "lp 7 x", "cs 400 i", "f1 8") count as one model number.
// Words without digits (like "hi-fi") are left alone.
const STOP_WORDS = new Set(['and', 'or', 'not', 'the', 'for', 'with', 'in', 'on', 'to', 'of', 'a', 'an', 'by', 'vs']);
const isModel = (w) => /\d/.test(w) && (/[a-z]/i.test(w) || /[\/-]/.test(w));
const squash = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const modelTokens = (query) => {
  const words = (query || '').replace(/["()]/g, ' ').trim().split(/\s+/).filter(Boolean);
  const tokens = [];
  words.forEach((w) => {
    const prev = tokens[tokens.length - 1];
    const last = prev ? prev.split(' ').pop() : '';
    const joins =
      prev &&
      !STOP_WORDS.has(last.toLowerCase()) &&
      !STOP_WORDS.has(w.toLowerCase()) &&
      ((/^[a-z]{1,4}$/i.test(last) && /^\d/.test(w)) || // "ht 1003", "lp 7"
        (/\d/.test(last) && /^[a-z]{1,2}$/i.test(w)) || // "lp7 x", "cs400 i", "4 tc"
        (/[a-z]\d*$/i.test(last) && /\d/.test(last) && /^\d+$/.test(w))); // "f1 8"
    if (joins) tokens[tokens.length - 1] = `${prev} ${w}`;
    else tokens.push(w);
  });
  return tokens;
};
TAT.searchCandidates = (query) => {
  const typed = (query || '').replace(/\s+/g, ' ').trim();
  if (!TAT.settings?.normaliseModels || !typed) return { candidates: [typed], models: [] };
  const tokens = modelTokens(typed);
  const models = tokens.filter(isModel).map(squash).filter(Boolean);
  if (!models.length) return { candidates: [typed], models };
  const sep = (t) => t.replace(/[\s\/-]+/g, ' ').trim();
  const forms = [
    (t) => sep(t).replace(/(?<=[a-z])(?=\d)|(?<=\d)(?=[a-z])/gi, ' '),
    sep,
    (t) => sep(t).replace(/ /g, ''),
  ];
  const build = (f) => tokens.map((t) => (isModel(t) ? f(t) : t)).join(' ').toLowerCase();
  const candidates = [typed.toLowerCase(), ...forms.map(build)];
  if (models.length === 1 && models[0].length <= 12) {
    const m = models[0];
    for (let i = 1; i < m.length; i += 1) candidates.push(build(() => `${m.slice(0, i)} ${m.slice(i)}`));
  }
  return { candidates: [...new Set(candidates)], models };
};
// Best single guess without asking Shopify (used for plain links)
TAT.normaliseQuery = (query) => TAT.searchCandidates(query).candidates[1] || query;
const resolved = new Map();
TAT.resolveSearch = async (query) => {
  const { candidates, models } = TAT.searchCandidates(query);
  if (!models.length || candidates.length < 2) return candidates[0];
  const key = candidates[0];
  if (resolved.has(key)) return resolved.get(key);
  const base = TAT.routes?.predictiveSearch || '/search/suggest';
  const hits = async (q) => {
    const params = new URLSearchParams({ q, 'resources[type]': 'product', 'resources[limit]': '10' });
    try {
      const res = await fetch(`${base}.json?${params}`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return false;
      const products = (await res.json())?.resources?.results?.products || [];
      return products.some((p) => models.every((m) => squash(`${p.title} ${p.handle}`).includes(m)));
    } catch {
      return false;
    }
  };
  // Three at a time, first match in order wins; usually settled by the first batch
  let best = candidates[0];
  for (let i = 0; i < candidates.length && i < 15; i += 3) {
    const batch = candidates.slice(i, i + 3);
    const found = await Promise.all(batch.map(hits));
    const at = found.indexOf(true);
    if (at !== -1) {
      best = batch[at];
      break;
    }
  }
  resolved.set(key, best);
  return best;
};

const cartRequest = async (url, body) => {
  const res = await fetch(url, {
    method: body ? 'POST' : 'GET',
    headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  // Rate limits and outages can return HTML; never show a JSON parse error to a customer.
  let json = {};
  try {
    json = await res.json();
  } catch {
    json = {};
  }
  if (!res.ok) {
    const generic = typeof json.message === 'string' && !/^cart error$/i.test(json.message) ? json.message : '';
    const message = json.description || generic || "Sorry, we couldn't update your cart. Please try again.";
    throw Object.assign(new Error(message), { status: res.status, json });
  }
  return json;
};

// Section ids of the cart areas on this page (drawer, cart page). They are requested with add so the
// fresh HTML arrives in the same response and the drawer never opens showing the old cart.
const cartSectionIds = () => [
  ...new Set(Array.from(document.querySelectorAll('[data-cart-root][data-section-id]'), (el) => el.dataset.sectionId)),
];

TAT.cart = {
  async get() {
    return cartRequest(`${TAT.routes.cart}.js`);
  },
  async add(body) {
    const ids = cartSectionIds();
    const payload =
      ids.length && !body.sections ? { ...body, sections: ids.join(','), sections_url: window.location.pathname } : body;
    const added = await cartRequest(`${TAT.routes.cartAdd}.js`, payload);
    const cart = await this.get();
    if (added?.sections) cart.sections = added.sections;
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, added } }));
    return cart;
  },
  async change(body) {
    const cart = await cartRequest(`${TAT.routes.cartChange}.js`, body);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    return cart;
  },
};

// --header-h: height of the header while it's sticky (mobile), 0 when it scrolls away (desktop).
// Used by bars that pin under the header, e.g. the collection toolbar.
const headerSection = document.querySelector('.header-section');
if (headerSection) {
  const setHeaderHeight = () => {
    const sticky = getComputedStyle(headerSection).position === 'sticky';
    document.documentElement.style.setProperty('--header-h', sticky ? `${headerSection.offsetHeight}px` : '0px');
  };
  setHeaderHeight();
  if ('ResizeObserver' in window) new ResizeObserver(setHeaderHeight).observe(headerSection);
  window.addEventListener('resize', setHeaderHeight, { passive: true });
}

// Phones: the sticky header slides away while scrolling down and comes back on the way up. It stays put near the
// top, while a drawer or pop-up is open and while anything in it has focus (e.g. typing a search).
if (headerSection) {
  const root = document.documentElement;
  let lastY = window.scrollY;
  let queued = false;
  const setAway = (away) => root.toggleAttribute('data-header-away', away);
  const update = () => {
    queued = false;
    const y = window.scrollY;
    const delta = y - lastY;
    const pinned =
      getComputedStyle(headerSection).position !== 'sticky' ||
      root.classList.contains('overflow-hidden') ||
      headerSection.contains(document.activeElement) ||
      y < headerSection.offsetHeight;
    if (pinned) setAway(false);
    else if (Math.abs(delta) > 6) setAway(delta > 0);
    else return;
    lastY = y;
  };
  window.addEventListener('scroll', () => {
    if (!queued) (queued = true), requestAnimationFrame(update);
  }, { passive: true });
  headerSection.addEventListener('focusin', () => setAway(false));
}

// Keep cart counts in sync anywhere on the page: <span data-cart-count>
document.addEventListener('cart:updated', (e) => {
  const count = e.detail?.cart?.item_count ?? 0;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.hidden = count === 0;
  });
});

/*
  <tat-drawer id="MenuDrawer" side="left"> ... </tat-drawer>
  Open with any element carrying data-drawer-open="MenuDrawer". Close with [data-drawer-close],
  the overlay, or Escape. Dispatches 'drawer:open' / 'drawer:close'.
*/
class TatDrawer extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    if (!this.hasAttribute('aria-hidden')) this.setAttribute('aria-hidden', 'true');
    this.setExpanded(this.classList.contains('is-open'));
    if (this.bound) return; // moved in the DOM: listeners are already attached
    this.bound = true;
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]') || e.target === this) this.close();
    });
    this.onKey = (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') this.trapFocus(e);
    };
  }
  disconnectedCallback() {
    // Re-rendered by the theme editor (or removed) while open: don't leave the page scroll-locked.
    if (!this.classList.contains('is-open')) return;
    document.removeEventListener('keydown', this.onKey);
    document.documentElement.classList.remove('overflow-hidden');
  }
  openers() {
    return this.id ? Array.from(document.querySelectorAll(`[data-drawer-open="${CSS.escape(this.id)}"]`)) : [];
  }
  setExpanded(expanded) {
    this.openers().forEach((el) => el.setAttribute('aria-expanded', String(expanded)));
  }
  open(opener) {
    // Menus kept in a <template> (the mobile menu) are built the first time the drawer opens.
    const template = this.querySelector('template[data-drawer-menu-template]');
    if (template) {
      this.querySelector('[data-drawer-menu]')?.append(template.content);
      template.remove();
      void this.offsetWidth; // lay the new items out hidden first, so their entrance animates on the first open
    }
    this.opener = opener || document.activeElement;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    this.setExpanded(true);
    document.documentElement.classList.add('overflow-hidden');
    document.removeEventListener('keydown', this.onKey);
    document.addEventListener('keydown', this.onKey);
    const focusable =
      this.querySelector('[autofocus]') ||
      this.querySelector('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea');
    // The drawer fades in by animating visibility, so the first frame can still be hidden: try again once it shows.
    // preventScroll keeps the page underneath exactly where it was.
    const focusIn = () => focusable?.focus({ preventScroll: true });
    requestAnimationFrame(focusIn);
    setTimeout(() => {
      if (this.classList.contains('is-open') && !this.contains(document.activeElement)) focusIn();
    }, 80);
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }
  trapFocus(e) {
    const focusable = Array.from(
      this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.getClientRects().length > 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!this.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
  close() {
    const wasOpen = this.classList.contains('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    this.setExpanded(false);
    document.documentElement.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKey);
    if (!wasOpen) return;
    // The opener may have been replaced while the drawer was open (e.g. the Filter button after a
    // filter change), so fall back to a visible opener for the same drawer.
    let target = this.opener;
    if (!target?.isConnected) target = this.openers().find((el) => el.getClientRects().length > 0);
    target?.focus?.({ preventScroll: true }); // don't scroll back to a header that has scrolled away
    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }
}
if (!customElements.get('tat-drawer')) customElements.define('tat-drawer', TatDrawer);

/*
  Mobile menu panels (snippets/mobile-nav-item, size 'large'): [data-menu-panel-open] (aria-controls = the panel id)
  slides its [data-menu-panel] in over the menu; [data-menu-panel-back] slides it away and returns focus. Open panels
  close when the menu drawer closes, so it always reopens on the main list.
*/
const closeMenuPanel = (panel, restoreFocus = false) => {
  if (!panel?.hasAttribute('data-open')) return;
  panel.removeAttribute('data-open');
  const opener = document.querySelector(`[aria-controls="${panel.id}"]`);
  opener?.setAttribute('aria-expanded', 'false');
  if (restoreFocus) opener?.focus({ preventScroll: true });
  const done = () => {
    if (panel.hasAttribute('data-open')) return;
    panel.hidden = true;
    panel.querySelector('tat-menu-filter')?.reset?.();
  };
  panel.addEventListener('transitionend', done, { once: true });
  setTimeout(done, 500);
};
document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-menu-panel-open]');
  if (opener) {
    const panel = document.getElementById(opener.getAttribute('aria-controls'));
    if (!panel) return;
    panel.hidden = false;
    void panel.offsetWidth; // start from off-screen so it slides in
    panel.setAttribute('data-open', '');
    opener.setAttribute('aria-expanded', 'true');
    panel.querySelector('[data-menu-panel-back]')?.focus({ preventScroll: true });
    return;
  }
  const back = e.target.closest('[data-menu-panel-back]');
  if (back) closeMenuPanel(back.closest('[data-menu-panel]'), true);
});
document.addEventListener('drawer:close', (e) => {
  if (e.target.id !== 'MenuDrawer') return;
  setTimeout(() => e.target.querySelectorAll('[data-menu-panel][data-open]').forEach((p) => closeMenuPanel(p)), 320);
});

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = document.getElementById(opener.dataset.drawerOpen);
  if (drawer?.open) {
    e.preventDefault();
    drawer.open(opener);
  }
});

/*
  <tat-scroll-row> wraps a .scroll-row track and optional [data-scroll-prev]/[data-scroll-next] buttons.
*/
class TatScrollRow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.scroll-row');
    if (!this.track) return;
    this.prev = this.querySelector('[data-scroll-prev]');
    this.next = this.querySelector('[data-scroll-next]');
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
    this.update();
  }
  go(dir) {
    this.track.scrollBy({ left: dir * this.track.clientWidth * 0.9, behavior: 'smooth' });
  }
  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    if (this.prev) this.prev.disabled = scrollLeft <= 2;
    if (this.next) this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
  }
}
if (!customElements.get('tat-scroll-row')) customElements.define('tat-scroll-row', TatScrollRow);

/*
  Desktop dropdown and mega menus (<details data-nav-dropdown>).
  - Mouse: opens after a short hover delay, closes after a longer one so the pointer can travel into the panel.
  - Click or keyboard: native <details> toggle. Opening a mega menu by click focuses its filter box.
  - Only one open at a time. Outside click or Escape closes; focus returns to the menu heading.
*/
const navDropdowns = () => Array.from(document.querySelectorAll('details[data-nav-dropdown]'));
const closeNav = (except) =>
  navDropdowns().forEach((o) => {
    if (o !== except && o.open) o.removeAttribute('open');
  });
const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

const initNavDropdown = (d) => {
  if (d.dataset.navReady) return;
  d.dataset.navReady = 'true';
  const li = d.closest('li') || d;
  const summary = d.querySelector('summary');
  let openTimer;
  let closeTimer;
  let openedByHover = false;

  li.addEventListener('mouseenter', () => {
    if (!canHover()) return;
    clearTimeout(closeTimer);
    const anotherOpen = navDropdowns().some((o) => o !== d && o.open);
    openTimer = setTimeout(() => {
      closeNav(d);
      if (!d.open) {
        openedByHover = true;
        d.setAttribute('open', '');
      }
    }, anotherOpen ? 0 : 90);
  });
  li.addEventListener('mouseleave', () => {
    if (!canHover()) return;
    clearTimeout(openTimer);
    // Keep the panel open while someone is typing in its filter box.
    if (d.contains(document.activeElement) && document.activeElement.matches('[data-menu-filter-input]')) return;
    closeTimer = setTimeout(() => d.removeAttribute('open'), 280);
  });
  summary?.addEventListener('click', (e) => {
    // A click on a panel that hover already opened keeps it open (and focuses the filter) instead of closing it.
    if (d.open && openedByHover) {
      e.preventDefault();
      openedByHover = false;
      d.querySelector('[data-menu-filter-input]')?.focus();
      return;
    }
    openedByHover = false;
  });
  d.addEventListener('toggle', () => {
    summary?.setAttribute('aria-expanded', d.open ? 'true' : 'false');
    if (d.open) {
      closeNav(d);
      if (!openedByHover) requestAnimationFrame(() => d.querySelector('[data-menu-filter-input]')?.focus({ preventScroll: true }));
    } else {
      openedByHover = false;
      d.querySelector('tat-menu-filter')?.reset();
    }
  });
  summary?.setAttribute('aria-expanded', 'false');
};
navDropdowns().forEach(initNavDropdown);
// Theme editor: the header section is re-rendered when its settings change.
document.addEventListener('shopify:section:load', () => navDropdowns().forEach(initNavDropdown));
document.addEventListener('click', (e) => {
  if (!e.target.closest('details[data-nav-dropdown]')) closeNav();
});
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const current = document.activeElement?.closest?.('details[data-nav-dropdown][open]');
  if (!current) return;
  current.removeAttribute('open');
  current.querySelector('summary')?.focus(); // keep keyboard users where they were
});

/*
  <tat-menu-filter>: live filter for mega menus (desktop panels and the mobile menu).
  Markup contract: [data-menu-filter-input], [data-menu-group] containing [data-menu-group-title] and
  [data-menu-item] entries, optional [data-menu-empty] with [data-menu-query] and [data-menu-search-link],
  optional [data-menu-count data-noun], [data-menu-body] scroller.
  Matching ignores case, accents and punctuation ("proj" finds Pro-Ject, "bw" and "b&w" find Bowers & Wilkins).
  Enter opens the first match, or searches the whole site when nothing matches.
*/
const menuKey = (text) =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
const menuInitials = (text) =>
  text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('');

class TatMenuFilter extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.input = this.querySelector('[data-menu-filter-input]');
    this.body = this.querySelector('[data-menu-body]') || this;
    this.empty = this.querySelector('[data-menu-empty]');
    this.count = this.querySelector('[data-menu-count]');
    this.total = this.querySelectorAll('[data-menu-item]').length;
    this.groups = Array.from(this.querySelectorAll('[data-menu-group]')).map((el) => {
      const title = el.querySelector('[data-menu-group-title]')?.textContent.trim() || '';
      const items = Array.from(el.querySelectorAll('[data-menu-item]')).map((item) => {
        const text = item.textContent.trim();
        return { el: item, key: `${menuKey(text)} ${menuInitials(text)}`, link: item.matches('a') ? item : item.querySelector('a') };
      });
      return { el, key: menuKey(title), items, details: el.querySelector('details') };
    });
    this.tabs = Array.from(this.querySelectorAll('[data-menu-tab]'));
    this.activeId = this.tabs[0]?.dataset.menuTab || null;
    this.tabs.forEach((tab, index) => {
      let timer;
      tab.addEventListener('mouseenter', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.activate(tab.dataset.menuTab), 60);
      });
      tab.addEventListener('mouseleave', () => clearTimeout(timer));
      tab.addEventListener('focus', () => this.activate(tab.dataset.menuTab));
      tab.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const next = this.tabs[(index + (e.key === 'ArrowDown' ? 1 : -1) + this.tabs.length) % this.tabs.length];
          next.focus();
        } else if (e.key === 'ArrowRight') {
          const pane = document.getElementById(tab.dataset.menuTab);
          const link = pane?.querySelector('[data-menu-item] a');
          if (link) {
            e.preventDefault();
            link.focus();
          }
        }
      });
    });

    if (!this.input) return;

    this.input.addEventListener('input', () => this.filter());
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const first = this.firstMatch();
        if (first) window.location.href = first.href;
        else if (this.input.value.trim()) window.location.href = this.searchHref(this.input.value.trim());
      } else if (e.key === 'Escape' && this.input.value) {
        e.stopPropagation();
        this.reset();
      } else if (e.key === 'ArrowDown') {
        const first = this.firstMatch();
        if (first) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  activate(id) {
    if (!id) return;
    this.activeId = id;
    this.tabs.forEach((t) => t.setAttribute('aria-selected', t.dataset.menuTab === id ? 'true' : 'false'));
    if (this.input && this.input.value.trim()) return; // while filtering, every matching group stays visible
    this.groups.forEach((g) => {
      g.el.hidden = g.el.id !== id;
    });
    if (this.body !== this) this.body.scrollTop = 0;
  }

  searchHref(q) {
    const base = this.dataset.searchUrl || '/search';
    const query = window.TAT?.normaliseQuery ? TAT.normaliseQuery(q) : q;
    return `${base}?q=${encodeURIComponent(query)}&type=product`;
  }

  firstMatch() {
    for (const g of this.groups) {
      if (g.el.hidden) continue;
      const item = g.items.find((i) => !i.el.hidden && i.link);
      if (item) return item.link;
    }
    return null;
  }

  filter() {
    const raw = this.input.value.trim();
    const q = menuKey(raw);
    let visible = 0;
    this.groups.forEach((g) => {
      const titleMatch = q !== '' && g.key.includes(q);
      let groupVisible = 0;
      g.items.forEach((i) => {
        const show = q === '' || titleMatch || i.key.includes(q);
        i.el.hidden = !show;
        if (show) groupVisible += 1;
      });
      g.el.hidden = q !== '' && groupVisible === 0 && !titleMatch;
      if (q !== '' && g.el.hasAttribute('data-menu-popular')) g.el.hidden = true; // no duplicates while filtering
      if (this.tabs.length) {
        if (q === '') g.el.hidden = g.el.id !== this.activeId;
        const tab = this.tabs.find((t) => t.dataset.menuTab === g.el.id);
        if (tab) tab.toggleAttribute('data-dim', q !== '' && g.el.hidden);
      }
      if (g.details) g.details.open = q !== '' && !g.el.hidden;
      visible += groupVisible;
    });
    // While filtering no category is "selected"; the previous selection comes back when the box is cleared.
    this.tabs?.forEach((t) => t.setAttribute('aria-selected', q === '' && t.dataset.menuTab === this.activeId ? 'true' : 'false'));
    if (this.empty) {
      this.empty.hidden = visible > 0;
      const qEl = this.empty.querySelector('[data-menu-query]');
      if (qEl) qEl.textContent = raw;
      const link = this.empty.querySelector('[data-menu-search-link]');
      if (link) link.href = this.searchHref(raw);
    }
    if (this.count) {
      const noun = this.count.dataset.noun || '';
      this.count.textContent = q ? `${visible} of ${this.total} ${noun}` : `${this.total} ${noun}`;
    }
    if (this.body !== this) this.body.scrollTop = 0;
  }

  reset() {
    if (!this.input || !this.input.value) return;
    this.input.value = '';
    this.filter();
  }
}
if (!customElements.get('tat-menu-filter')) customElements.define('tat-menu-filter', TatMenuFilter);

/* ------------------------------------------------------------------ */
/* Read more: clamped copy with a fade ([data-readmore])                */
/* ------------------------------------------------------------------ */
// Delegated, so it keeps working after a theme editor re-render. Blocks whose copy already fits are shown
// in full with no button.
document.addEventListener('click', (e) => {
  const toggle = e.target.closest('[data-readmore-toggle]');
  const body = toggle?.closest('[data-readmore]')?.querySelector('[data-readmore-body]');
  if (!body) return;
  const expanded = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!expanded));
  toggle.textContent = expanded ? toggle.dataset.labelMore || 'Read more' : toggle.dataset.labelLess || 'Read less';
  body.toggleAttribute('data-expanded', !expanded);
  if (expanded) body.closest('[data-readmore]')?.scrollIntoView({ block: 'nearest' });
});

function fitReadmore(scope = document) {
  scope.querySelectorAll('[data-readmore]').forEach((wrap) => {
    const body = wrap.querySelector('[data-readmore-body]');
    const toggle = wrap.querySelector('[data-readmore-toggle]');
    if (!body || !toggle || wrap.dataset.readmoreReady) return;
    wrap.dataset.readmoreReady = 'true';
    const fit = () => {
      if (body.hasAttribute('data-expanded')) return;
      const fits = body.scrollHeight <= body.clientHeight + 24;
      toggle.hidden = fits;
      body.style.maskImage = fits ? 'none' : '';
    };
    fit();
    // Content can arrive later (app widgets, images), so re-check whenever the body changes size.
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(fit);
      ro.observe(body);
      if (body.firstElementChild) ro.observe(body.firstElementChild);
    }
  });
}
if (document.readyState === 'complete') fitReadmore();
else window.addEventListener('load', () => fitReadmore());
document.addEventListener('shopify:section:load', (e) => fitReadmore(e.target));

/* ------------------------------------------------------------------ */
/* Enquiry modals (snippets/enquiry-modals.liquid)                      */
/* ------------------------------------------------------------------ */
// Opened by [data-modal-open="contact|booking"] (data-topic preselects a contact topic, data-message sets the starter
// message), by plain links to the contact page (contact) and by any link ending in #book-a-session (booking).
// Contact links in the site chrome (header, top bar, mobile menu, footer) go to the contact page itself; links in the
// page body and in drawers open the pop-up. There is no booking page, so booking links open the pop-up everywhere, and
// arriving on any page with #book-a-session opens it too.
const MODALS = { contact: 'ContactModal', booking: 'BookingModal' };
const SITE_CHROME = '.promo-topbar-section, .header-section, .footer-section, [data-site-chrome]';
const SENT_KEY = 'tat-enquiry-sent';

function openModal(kind, opener) {
  const dialog = document.getElementById(MODALS[kind]);
  if (!dialog || dialog.open) return false;
  // Browsers without <dialog> (Safari before 15.4): links carry on to the contact page; buttons are sent there.
  if (typeof dialog.showModal !== 'function') {
    if (opener && !opener.closest?.('a[href]')) location.href = document.getElementById('ContactModal')?.dataset.contactPath || '/pages/contact';
    return false;
  }
  const topic = opener?.dataset?.topic;
  if (topic) {
    const radio = [...dialog.querySelectorAll('input[name="contact[Enquiry type]"]')].find((r) => r.value === topic);
    if (radio) radio.checked = true;
  }
  // data-hear fills the booking form's "Would like to hear" field (e.g. from a review article), unless it's filled.
  const hear = opener?.dataset?.hear;
  const hearField = dialog.querySelector('input[name="contact[Would like to hear]"]');
  if (hear && hearField && !hearField.value.trim()) hearField.value = hear;
  // data-message replaces the starter message, unless the customer has already typed their own.
  const message = opener?.dataset?.message;
  const body = dialog.querySelector('[data-enquiry-message]');
  if (message && body && (!body.value.trim() || body.value === body.defaultValue || body.dataset.prefilled === 'true')) {
    body.value = message;
    body.dataset.prefilled = 'true';
  }
  if (dialog.querySelector('[data-enquiry-posted]')) showSent(dialog);
  // data-cart-message: start the message with what's in the cart right now.
  if (opener?.hasAttribute?.('data-cart-message') && body && TAT.cart) {
    TAT.cart
      .get()
      .then((cart) => {
        if (!cart?.items?.length || body.dataset.prefilled === 'false') return;
        const lines = cart.items.map(
          (item) => `- ${item.quantity} x ${item.product_title}${item.variant_title ? ` (${item.variant_title})` : ''}`
        );
        body.value = `Hi, I have a question about the items in my cart:\n${lines.join('\n')}\n\n`;
        body.dataset.prefilled = 'true';
      })
      .catch(() => {});
  }
  dialog.showModal();
  document.documentElement.classList.add('overflow-hidden');
  return true;
}

function showSent(dialog) {
  const form = dialog.querySelector('[data-enquiry-form]');
  const success = dialog.querySelector('[data-enquiry-success]');
  if (form) form.hidden = true;
  if (success) {
    success.hidden = false;
    success.focus?.();
  }
}

document.addEventListener('click', (e) => {
  const closer = e.target.closest('[data-modal-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }
  if (e.target.matches?.('dialog[data-enquiry-modal]')) {
    e.target.close(); // backdrop click
    return;
  }
  const opener = e.target.closest('[data-modal-open]');
  let kind = opener?.dataset.modalOpen;
  const inChrome = !!e.target.closest(SITE_CHROME);
  if (kind === 'contact' && inChrome && opener.matches('a[href]')) return; // header, menu and footer: go to the page
  if (!kind) {
    const link = e.target.closest('a[href]');
    const path = document.getElementById('ContactModal')?.dataset.contactPath;
    if (!link || !path || link.hasAttribute('data-no-modal') || e.metaKey || e.ctrlKey || e.shiftKey) return;
    let url;
    try { url = new URL(link.href, location.href); } catch { return; }
    if (url.origin !== location.origin) return;
    if (url.hash === '#book-a-session') kind = 'booking';
    else if (url.pathname === path && location.pathname !== path && !inChrome) kind = 'contact';
    else return;
  }
  if (openModal(kind, opener || e.target.closest('a'))) e.preventDefault();
});

document.querySelectorAll('dialog[data-enquiry-modal]').forEach((dialog) => {
  dialog.addEventListener('close', () => document.documentElement.classList.remove('overflow-hidden'));
});
if (location.hash === '#book-a-session') openModal('booking');

// Booking: selected days go into one field, and dates we're closed are flagged.
function syncBookingDays(form) {
  const field = form.querySelector('[data-booking-days]');
  if (!field) return;
  field.value = [...form.querySelectorAll('[data-booking-day]:checked')].map((c) => c.value).join(', ') || 'Any';
}
document.addEventListener('change', (e) => {
  const form = e.target.closest?.('[data-enquiry-form]');
  if (!form) return;
  if (e.target.matches('[data-booking-day]')) syncBookingDays(form);
  if (e.target.matches('[data-booking-date]')) {
    const note = form.querySelector('[data-booking-date-note]');
    const value = e.target.value;
    const closed = !!value && !!TAT.storeHours && !TatOpenStatus.hoursOn(value);
    note?.classList.toggle('hidden', !closed);
    if (closed) e.target.value = '';
  }
});

// Validate first, in the capture phase, so an incomplete form never reaches Shopify's spam check (which listens on
// the form and posts it). A complete form then posts normally through that check, and the page comes back with
// ?contact_posted=true (or the form's errors), where the modal reopens on its thank-you message or errors.
document.addEventListener(
  'submit',
  (e) => {
    const form = e.target.closest?.('[data-enquiry-form]');
    if (!form) return;
    const errors = form.querySelector('[data-enquiry-errors]');
    const invalid = [...form.querySelectorAll('[required]')].find((f) => !f.value.trim() || !f.checkValidity());
    if (invalid) {
      e.preventDefault();
      e.stopImmediatePropagation();
      errors.textContent =
        'Please fill in your name, a valid email' +
        (form.querySelector('[name="contact[phone]"][required]') ? ', your phone number' : '') +
        (form.querySelector('[name="contact[body]"][required]') ? ' and a message.' : '.');
      invalid.focus();
      return;
    }
    errors.textContent = '';
    syncBookingDays(form);
    const button = form.querySelector('[data-enquiry-submit]');
    if (button) button.textContent = 'Sending...';
    try {
      sessionStorage.setItem(SENT_KEY, form.closest('dialog')?.id || form.id);
    } catch {}
  },
  true
);

/* ------------------------------------------------------------------ */
/* Lead tracking: where visitors came from, and confirmed enquiries     */
/* ------------------------------------------------------------------ */
// Attribution: UTMs, click ids and the referrer are kept in this browser (first visit and latest visit), and every
// Shopify contact form (enquiries, bookings, shortlist, trade-in) sends them with the enquiry as contact[...] fields,
// so they arrive in the store's enquiry email. Leads: after Shopify confirms a post (?contact_posted=true), a
// dataLayer event goes to GTM: generate_lead for enquiries, sign_up for the newsletter.
const ATTR_KEY = 'tat-attribution';
const LEAD_KEY = 'tat-lead';
const ATTR_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'];
try {
  const params = new URLSearchParams(location.search);
  const found = Object.fromEntries(ATTR_PARAMS.filter((k) => params.get(k)).map((k) => [k, params.get(k).slice(0, 200)]));
  let ref = '';
  try { ref = document.referrer && new URL(document.referrer).host !== location.host ? document.referrer.slice(0, 300) : ''; } catch {}
  if (Object.keys(found).length || ref) {
    const visit = { ...found, referrer: ref, landing: location.pathname.slice(0, 200), at: new Date().toISOString().slice(0, 10) };
    const saved = JSON.parse(localStorage.getItem(ATTR_KEY) || '{}');
    localStorage.setItem(ATTR_KEY, JSON.stringify({ first: saved.first || visit, last: visit }));
  }
} catch {}

const describeVisit = (v) => {
  if (!v) return '';
  const source = v.utm_source ? [v.utm_source, v.utm_medium].filter(Boolean).join(' / ')
    : v.gclid || v.gbraid || v.wbraid ? 'google / cpc'
    : v.fbclid ? 'facebook / paid or social'
    : v.msclkid ? 'bing / cpc'
    : v.referrer ? (() => { try { return new URL(v.referrer).host.replace(/^www\./, '') + ' / referral'; } catch { return 'referral'; } })()
    : 'direct';
  return `${source}${v.utm_campaign ? `, campaign ${v.utm_campaign}` : ''} (landed on ${v.landing || '/'}, ${v.at || ''})`;
};

document.addEventListener(
  'submit',
  (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const type = form.querySelector('input[name="form_type"]')?.value;
    if (type !== 'contact' && type !== 'customer') return;
    try {
      const formName = form.querySelector('[name="contact[Form]"]')?.value || (form.id.includes('trade') ? 'Trade-in' : form.id || 'Contact form');
      sessionStorage.setItem(LEAD_KEY, JSON.stringify({ formId: form.id, formType: formName, kind: type === 'customer' ? 'sign_up' : 'generate_lead' }));
    } catch {}
    if (type !== 'contact') return;
    let attr = {};
    try { attr = JSON.parse(localStorage.getItem(ATTR_KEY) || '{}'); } catch {}
    const last = attr.last;
    const fields = {
      'Lead source': describeVisit(last) || 'direct (no campaign or referrer seen in this browser)',
      'First visit': attr.first && JSON.stringify(attr.first) !== JSON.stringify(last) ? describeVisit(attr.first) : '',
      'Click ID': last ? ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'].filter((k) => last[k]).map((k) => `${k}: ${last[k]}`).join(', ') : '',
      'UTM term': last?.utm_term || '',
      'UTM content': last?.utm_content || '',
      'Referrer': last?.referrer || '',
      'Sent from page': location.pathname,
    };
    form.querySelectorAll('[data-attribution]').forEach((el) => el.remove());
    for (const [name, value] of Object.entries(fields)) {
      if (!value) continue;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `contact[${name}]`;
      input.value = value;
      input.setAttribute('data-attribution', '');
      form.append(input);
    }
  },
  // Capture phase: Shopify's spam check cancels the normal submit and posts the form itself, so a later listener
  // would miss it. Harmless if the post is then stopped: the lead only counts once Shopify confirms it.
  true
);

try {
  const lead = JSON.parse(sessionStorage.getItem(LEAD_KEY) || 'null');
  if (lead) {
    sessionStorage.removeItem(LEAD_KEY);
    const confirmed = location.search.includes(lead.kind === 'sign_up' ? 'customer_posted=true' : 'contact_posted=true');
    if (confirmed) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: lead.kind, formId: lead.formId, formType: lead.formType });
    }
  }
} catch {}

// Back from posting: reopen the modal that was sent, on its thank-you message (or with its errors).
try {
  const sent = sessionStorage.getItem(SENT_KEY);
  if (sent) {
    sessionStorage.removeItem(SENT_KEY);
    const kind = Object.keys(MODALS).find((k) => MODALS[k] === sent);
    const dialog = document.getElementById(sent);
    const posted = location.search.includes('contact_posted=true');
    const failed = dialog?.querySelector('[data-enquiry-errors]')?.textContent.trim();
    if (kind && (posted || failed)) openModal(kind);
    // A form on the page itself (the contact page): swap it for its thank-you, or bring its errors into view.
    const success = !kind && document.querySelector(`[data-page-enquiry-success="${sent}"]`);
    // A form inside an overlay (the shortlist): open it again first.
    const host = !kind && dialog?.closest('tat-drawer');
    if (host && (posted || failed)) host.open();
    if (success && posted) {
      dialog.hidden = true;
      success.hidden = false;
      success.scrollIntoView({ block: 'center' });
      success.focus({ preventScroll: true });
    } else if (!kind && failed) dialog?.scrollIntoView({ block: 'center' });
    if (posted) {
      const url = new URL(location.href);
      url.searchParams.delete('contact_posted');
      history.replaceState(history.state, '', url.pathname + url.search);
    }
  }
} catch {}

// Product pages: keep "You're asking about" in step with the selected variant.
document.addEventListener('variant:change', (e) => {
  const variant = e.detail?.variant;
  const field = document.querySelector('#ContactModal [data-enquiry-product]');
  if (!variant || !field) return;
  const title = (variant.options || []).join(' / ');
  field.value = title ? `${field.dataset.base} (${title})` : field.dataset.base;
  const label = document.querySelector('#ContactModal [data-enquiry-variant]');
  if (label && title) label.textContent = title;
});

/* ------------------------------------------------------------------ */
/* Opening hours: <tat-open-status> and <tat-special-hours>             */
/* ------------------------------------------------------------------ */
// The hours come from snippets/data-hours, written into the layout as #StoreHours:
// { week: { 0 (Sunday) to 6: "9:00-17:00" or "" for closed }, special: { "YYYY-MM-DD": "9:00-13:00" or "closed" } }.
// Everything here runs on the showroom's clock in Murarrie. Queensland has no daylight saving, so Brisbane time is
// UTC+10 all year, read with the UTC getters.
TAT.storeHours = (() => {
  try {
    const data = JSON.parse(document.getElementById('StoreHours').textContent);
    return { week: data.week || {}, special: data.special || {} };
  } catch {
    return null;
  }
})();

// <tat-open-status>: live "Open now" status. Special hours (public holidays) replace the regular hours on their
// date, and when we're closed it points to the next day we open, skipping closed days.
class TatOpenStatus extends HTMLElement {
  connectedCallback() {
    if (!TAT.storeHours) return;
    this.render();
    this.timer = setInterval(() => this.render(), 60000);
  }
  disconnectedCallback() {
    clearInterval(this.timer);
  }
  // Brisbane wall-clock time as a Date whose UTC fields read as Brisbane time.
  static brisbane(now = Date.now()) {
    return new Date(now + 10 * 36e5);
  }
  static dateKey(d) {
    return d.toISOString().slice(0, 10);
  }
  // Opening hours on a date (a "YYYY-MM-DD" key or a Brisbane Date): special hours first, then the regular week.
  static hoursOn(day) {
    const d = typeof day === 'string' ? new Date(`${day}T00:00:00Z`) : day;
    const key = TatOpenStatus.dateKey(d);
    const { week, special } = TAT.storeHours;
    return TatOpenStatus.parse(key in special ? special[key] : week[d.getUTCDay()]);
  }
  static parse(range) {
    const [open, close] = String(range || '').split('-').map((t) => {
      const [h, m = '0'] = t.trim().split(':');
      return Number(h) * 60 + Number(m);
    });
    return Number.isFinite(open) && Number.isFinite(close) ? { open, close } : null;
  }
  static label(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const h12 = h % 12 || 12;
    return `${h12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'am' : 'pm'}`;
  }
  static status(now = Date.now()) {
    const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const b = TatOpenStatus.brisbane(now);
    const minutes = b.getUTCHours() * 60 + b.getUTCMinutes();
    const today = TatOpenStatus.hoursOn(b);
    if (today && minutes >= today.open && minutes < today.close) {
      const until = TatOpenStatus.label(today.close);
      return { open: true, detail: `Until ${until} today`, short: `until ${until}` };
    }
    if (today && minutes < today.open) {
      const opens = TatOpenStatus.label(today.open);
      return { open: false, detail: `Opens today, ${opens}`, short: `Opens ${opens} today` };
    }
    // Look ahead up to three weeks (a run of public holidays plus closed days); a week or more away adds the date.
    for (let i = 1; i <= 21; i++) {
      const d = new Date(b.getTime() + i * 864e5);
      const next = TatOpenStatus.hoursOn(d);
      if (!next) continue;
      const name = names[d.getUTCDay()];
      const when = i === 1 ? 'tomorrow' : i < 7 ? name : `${name} ${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
      const opens = TatOpenStatus.label(next.open);
      return { open: false, detail: `Opens ${when}, ${opens}`, short: `Opens ${opens} ${when}` };
    }
    return null;
  }
  render() {
    const status = TatOpenStatus.status();
    if (!status) return;
    const { open, detail, short } = status;
    // data-compact (header): "Open now · until 5pm" or just "Opens 9am tomorrow", no "Closed" label.
    if (this.hasAttribute('data-compact')) {
      this.querySelector('[data-open-label]').textContent = open ? 'Open now' : short;
      this.querySelector('[data-open-detail]').textContent = open ? short : '';
      this.querySelectorAll('[data-open-sep]').forEach((el) => (el.hidden = !open));
    } else {
      this.querySelector('[data-open-label]').textContent = open ? 'Open now' : 'Closed';
      this.querySelector('[data-open-detail]').textContent = detail;
    }
    this.querySelector('[data-open-badge]')?.setAttribute('data-state', open ? 'open' : 'closed');
    this.dataset.state = open ? 'open' : 'closed';
    this.hidden = false;
  }
}
if (!customElements.get('tat-open-status')) customElements.define('tat-open-status', TatOpenStatus);

// <tat-special-hours data-from data-until> (snippets/store-hours-special): a special hours notice, shown from
// data-from until data-until ends, Brisbane time. The server renders it hidden outside those dates; checking again
// here means a page from Shopify's cache (or a section loaded later) still switches on the right day.
class TatSpecialHours extends HTMLElement {
  connectedCallback() {
    const today = TatOpenStatus.dateKey(TatOpenStatus.brisbane());
    const { from = '', until = '' } = this.dataset;
    if (from && until) this.hidden = !(today >= from && today <= until);
  }
}
if (!customElements.get('tat-special-hours')) customElements.define('tat-special-hours', TatSpecialHours);

/* ------------------------------------------------------------------ */
/* Phone action bar (snippets/mobile-action-bar.liquid)                 */
/* ------------------------------------------------------------------ */
// Shows once the visitor scrolls, hides while they type in a form (so it never sits over the keyboard). The first
// button is Call while open and Contact after hours (following <tat-open-status>); the second is Directions for
// nearby visitors (Cloudflare data centre from /cdn-cgi/trace, once a session) and Enquire for everyone else.
class TatActionBar extends HTMLElement {
  connectedCallback() {
    if (this.ready || !window.matchMedia('(max-width: 47.99rem)').matches) return;
    this.ready = true;
    this.primary = this.querySelector('[data-bar-primary]');
    this.status = this.querySelector('tat-open-status');
    this.nearby = false;
    this.apply();
    new MutationObserver(() => this.apply()).observe(this.status, { attributes: true, attributeFilter: ['data-state'] });
    this.locate();

    let queued = false;
    const check = () => {
      queued = false;
      const typing = document.activeElement?.matches?.('input:not([type=checkbox]):not([type=radio]), textarea, select');
      this.show(window.scrollY > 160 && !typing);
    };
    const queue = () => { if (!queued) (queued = true), requestAnimationFrame(check); };
    window.addEventListener('scroll', queue, { passive: true });
    document.addEventListener('focusin', queue);
    document.addEventListener('focusout', () => setTimeout(queue, 50));
    check();

    this.addEventListener('click', (e) => {
      const el = e.target.closest('a, button');
      if (!el) return;
      const event = el.hasAttribute('data-bar-directions') ? 'directions'
        : el.hasAttribute('data-bar-enquire') ? 'enquire'
        : this.open ? 'call' : 'contact';
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: `action_bar_${event}`, nearby: this.nearby });
    });
  }
  show(on) {
    if (on === this.hasAttribute('data-shown')) return;
    this.toggleAttribute('data-shown', on);
    this.inert = !on;
  }
  async locate() {
    const codes = (this.dataset.nearby || '').split(/[\s,]+/).filter(Boolean);
    if (!codes.length) return;
    let colo = null;
    try { colo = sessionStorage.getItem('tat-colo'); } catch {}
    if (!colo) {
      try {
        const text = await fetch('/cdn-cgi/trace', { cache: 'no-store' }).then((r) => (r.ok ? r.text() : ''));
        colo = text.match(/^colo=([A-Z]+)$/m)?.[1] || 'none';
        try { sessionStorage.setItem('tat-colo', colo); } catch {}
      } catch {
        return;
      }
    }
    this.nearby = codes.includes(colo);
    this.apply();
  }
  apply() {
    this.open = this.status?.dataset.state !== 'closed';
    const p = this.primary;
    p.href = this.open ? p.dataset.tel : p.dataset.contact;
    p.querySelector('[data-bar-label]').textContent = this.open ? 'Call us' : 'Contact us';
    p.querySelector('[data-bar-icon-call]').hidden = !this.open;
    p.querySelector('[data-bar-icon-contact]').hidden = this.open;
    const directions = this.querySelector('[data-bar-directions]');
    const enquire = this.querySelector('[data-bar-enquire]');
    const useDirections = this.nearby && !!directions;
    if (directions) directions.hidden = !useDirections;
    // After hours, Contact already covers enquiring, so visitors who aren't nearby get the one button.
    enquire.hidden = useDirections || !this.open;
    this.querySelector('[data-bar-grid]').toggleAttribute('data-single', !useDirections && !this.open);
  }
}
if (!customElements.get('tat-action-bar')) customElements.define('tat-action-bar', TatActionBar);
document.addEventListener('input', (e) => {
  if (e.target.matches?.('[data-enquiry-message]')) e.target.dataset.prefilled = 'false';
});

/* ------------------------------------------------------------------ */
/* Policy pages: "On this page" list from the section headings          */
/* ------------------------------------------------------------------ */
document.querySelectorAll('[data-legal]').forEach((legal) => {
  const toc = legal.querySelector('[data-legal-toc]');
  const list = legal.querySelector('[data-legal-toc-list]');
  // Shopify's own policies are plain paragraphs: style short ALL-CAPS lines as section headings and "-----" lines
  // as rules (presentation only, the text is unchanged). A heading repeating the page title is left as is.
  const title = legal.querySelector('h1')?.textContent.trim().toLowerCase();
  legal.querySelectorAll('.shopify-policy__body p').forEach((p) => {
    const text = p.textContent.trim();
    if (/^-{3,}$/.test(text)) p.classList.add('policy-rule');
    else if (text.length <= 90 && text.toLowerCase() !== title && (/^SECTION \d+\b/.test(text) || (/[A-Z]/.test(text) && text === text.toUpperCase()))) p.classList.add('policy-heading');
  });
  const headings = [...legal.querySelectorAll('.legal-body h4, .legal-body .shopify-policy__body h2, .legal-body .shopify-policy__body h3, .legal-body .policy-heading')];
  const dark = toc?.dataset.tone === 'dark';
  if (!toc || !list || headings.length < 2) return;
  const links = headings.map((h, i) => {
    if (!h.id) h.id = `section-${i + 1}-${h.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = `#${h.id}`;
    a.textContent = h.textContent.trim();
    a.className = dark
      ? '-ml-px block border-l border-transparent py-1.5 pl-4 text-white/55 no-underline transition-colors hover:text-white aria-[current=true]:border-accent aria-[current=true]:text-white'
      : '-ml-px block border-l border-transparent py-1.5 pl-4 text-muted no-underline transition-colors hover:text-navy aria-[current=true]:border-navy aria-[current=true]:font-semibold aria-[current=true]:text-navy';
    li.append(a);
    list.append(li);
    return a;
  });
  toc.hidden = false;
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const i = headings.indexOf(entry.target);
        links.forEach((a, j) => (j === i ? a.setAttribute('aria-current', 'true') : a.removeAttribute('aria-current')));
        const active = links[i];
        if (active && list.scrollHeight > list.clientHeight) {
          list.scrollTop += active.getBoundingClientRect().top - list.getBoundingClientRect().top - list.clientHeight / 2;
        }
      });
    },
    { rootMargin: '0px 0px -70% 0px' }
  );
  headings.forEach((h) => io.observe(h));
});

/* ------------------------------------------------------------------ */
/* Simple dialogs: [data-dialog-open="id"] opens <dialog id="id" data-dialog> */
/* ------------------------------------------------------------------ */
document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.dialogOpen);
    if (dialog?.showModal && !dialog.open) {
      dialog.showModal();
      document.documentElement.classList.add('overflow-hidden');
    }
    return;
  }
  if (e.target.closest('[data-dialog-close]')) e.target.closest('dialog')?.close();
  else if (e.target.matches?.('dialog[data-dialog]')) e.target.close();
});
document.querySelectorAll('dialog[data-dialog]').forEach((d) =>
  d.addEventListener('close', () => document.documentElement.classList.remove('overflow-hidden'))
);

/* Gallery "Show more photos": reveals the tiles held back by the section */
document.addEventListener('click', (e) => {
  const button = e.target.closest('[data-gallery-more]');
  if (!button) return;
  const list = document.getElementById(button.getAttribute('aria-controls'));
  list?.querySelectorAll('[data-gallery-extra]').forEach((el) => el.classList.remove('hidden'));
  button.setAttribute('aria-expanded', 'true');
  button.hidden = true;
});

/*
  Homepage offers grid: no gaps, however many offers are live. Two columns from 64rem. A lone half width offer
  (the last one, or one followed by a full width offer) is stretched to a full row (.promo-fill), and the grid
  holds back every offer after the first "after" ("Show more offers", .promo-extra), stretching the last one shown
  when it would sit alone in its row. The button appears only when more live offers exist than the grid shows.
  Order, once per page view: newest offer (latest start) first, ties in file order. Entries with home_slot
  (data-promo-slot, e.g. "6-9") take those positions instead, shuffled; entries sharing a slot beyond its size go
  after the rest.
*/
const promoTwoCols = window.matchMedia('(min-width: 64rem)');
function orderPromoGrid(grid) {
  if (grid.dataset.ordered) return;
  grid.dataset.ordered = 'true';
  const all = [...grid.children];
  const live = all.filter((el) => !el.hidden);
  const start = (el) => Number(el.dataset.promoStart) || 0;
  const order = live.filter((el) => !el.dataset.promoSlot).sort((a, b) => start(b) - start(a));
  const slots = new Map();
  live.filter((el) => el.dataset.promoSlot).forEach((el) => {
    const key = el.dataset.promoSlot;
    slots.set(key, [...(slots.get(key) || []), el]);
  });
  const spare = [];
  [...slots.entries()]
    .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
    .forEach(([key, els]) => {
      const [from, to = from] = key.split('-').map((n) => parseInt(n, 10));
      for (let i = els.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [els[i], els[j]] = [els[j], els[i]];
      }
      const size = Math.max(1, to - from + 1);
      order.splice(Math.min(Math.max(from - 1, 0), order.length), 0, ...els.slice(0, size));
      spare.push(...els.slice(size));
    });
  [...order, ...spare, ...all.filter((el) => el.hidden)].forEach((el) => grid.appendChild(el));
}
function layoutPromoGrid(grid, after) {
  orderPromoGrid(grid);
  const items = [...grid.querySelectorAll(':scope > :not([hidden])')];
  grid.querySelectorAll(':scope > .promo-fill, :scope > .promo-extra').forEach((el) => el.classList.remove('promo-fill', 'promo-extra'));
  if (promoTwoCols.matches) {
    const isFull = (el) => getComputedStyle(el).gridColumnEnd === '-1';
    let half = null; // a half width offer waiting for a partner
    items.forEach((el) => {
      if (isFull(el)) {
        half?.classList.add('promo-fill');
        half = null;
      } else if (half) half = null;
      else half = el;
    });
    half?.classList.add('promo-fill');
    // Hold back everything after "after" offers; the last one shown takes a full row if it has no partner
    let col = 0;
    for (let i = 0; i < items.length; i++) {
      const full = isFull(items[i]) || items[i].classList.contains('promo-fill');
      col = full ? 0 : col ^ 1;
      if (i + 1 >= after && i + 1 < items.length) {
        if (col === 1) items[i].classList.add('promo-fill');
        items.slice(i + 1).forEach((el) => el.classList.add('promo-extra'));
        break;
      }
    }
  } else {
    items.slice(after).forEach((el) => el.classList.add('promo-extra'));
  }
  grid.classList.add('is-sized');
}
function initPromoMore(root = document) {
  root.querySelectorAll('[data-promo-more]').forEach((button) => {
    const grid = document.getElementById(button.getAttribute('aria-controls'));
    const wrap = button.closest('[data-promo-more-wrap]');
    if (!grid || !wrap) return;
    const open = grid.classList.contains('is-open');
    layoutPromoGrid(grid, open ? Infinity : Number(button.dataset.promoMore));
    if (open) return;
    const extra = grid.querySelectorAll(':scope > .promo-extra').length;
    wrap.hidden = extra <= 0;
    button.textContent = `Show ${extra} more ${extra === 1 ? 'offer' : 'offers'}`;
  });
}
promoTwoCols.addEventListener('change', () => initPromoMore());
initPromoMore();
window.addEventListener('load', () => initPromoMore());
document.addEventListener('shopify:section:load', (e) => initPromoMore(e.target));
document.addEventListener('click', (e) => {
  const button = e.target.closest('[data-promo-more]');
  if (!button) return;
  const grid = document.getElementById(button.getAttribute('aria-controls'));
  if (grid) {
    grid.classList.add('is-open');
    layoutPromoGrid(grid, Infinity);
  }
  button.setAttribute('aria-expanded', 'true');
  button.closest('[data-promo-more-wrap]').hidden = true;
});

/*
  <tat-review-wall>: swaps the hand-picked reviews for live 5-star reviews from Stamped's public widget API when
  the section nears the screen (data-api-key and data-store set by sections/home-review-wall.liquid). Cards are
  built from the section's <template data-review-card>, split across the rows, and each row is output twice
  so its marquee loops seamlessly. Also updates the average and total. On any failure the static reviews stay.
*/
class TatReviewWall extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.apiKey || this.loaded) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '800px 0px' }
    );
    // Watch the section: on product pages the wall sits in a band that stays hidden until reviews load.
    this.observer.observe(this.closest('section') || this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    this.loaded = true;
    const params = new URLSearchParams({ storeUrl: this.dataset.store, apiKey: this.dataset.apiKey, take: '100', page: '1' });
    try {
      const response = await fetch(`https://stamped.io/api/widget/reviews?${params}`);
      if (!response.ok) return;
      const data = await response.json();
      const seen = new Set();
      const reviews = (data.data || []).filter((r) => {
        const text = (r.reviewMessage || '').trim();
        if (r.reviewRating < 5 || text.length < 50 || text.length > 360 || seen.has(text)) return false;
        seen.add(text);
        return true;
      });
      const rows = [...this.querySelectorAll('[data-review-row]')];
      if (reviews.length < rows.length * 6) return;
      this.render(reviews, rows);
      this.closest('[data-review-band]')?.removeAttribute('hidden');
      const section = this.closest('section');
      const average = Number(data.ratingAll || data.rating);
      if (average) section?.querySelector('[data-review-average]')?.replaceChildren(average.toFixed(1));
      if (data.totalAll) section?.querySelector('[data-review-count]')?.replaceChildren(Number(data.totalAll).toLocaleString('en-AU'));
    } catch {
      // Keep the hand-picked reviews.
    }
  }

  render(reviews, rows) {
    const template = this.querySelector('template[data-review-card]');
    if (!template) return;
    const card = (r) => {
      const node = template.content.firstElementChild.cloneNode(true);
      const set = (field, value) => {
        const el = node.querySelector(`[data-field="${field}"]`);
        if (!el) return;
        if (value) el.textContent = value;
        else el.remove();
      };
      const name = (r.author || 'Verified buyer').trim();
      set('title', (r.reviewTitle || '').trim());
      set('text', r.reviewMessage.trim());
      set('name', name);
      set('initial', name.charAt(0).toUpperCase());
      set('product', (r.productName || '').split(' - ').slice(0, 2).join(' '));
      return node;
    };
    rows.forEach((row, index) => {
      const share = reviews.filter((_, i) => i % rows.length === index);
      const track = row.querySelector('[data-review-track]');
      const lists = track?.querySelectorAll(':scope > ul');
      if (!track || lists.length < 2) return;
      lists.forEach((list) => list.replaceChildren(...share.map(card)));
      track.style.animationDuration = `${Math.max(40, share.length * 12)}s`;
    });
  }
}
if (!customElements.get('tat-review-wall')) customElements.define('tat-review-wall', TatReviewWall);

/*
  Customer review photos: a[data-review-photo] opens #ReviewPhotoDialog (snippets/review-photo-dialog) instead of
  following the link. Previous/next move through the unique photos in the same [data-photo-group]; arrow keys and
  Esc work too. data-caption and data-who fill the caption.
*/
const photoViewer = { list: [], index: 0 };
function showReviewPhoto(dialog, index) {
  const { list } = photoViewer;
  photoViewer.index = (index + list.length) % list.length;
  const link = list[photoViewer.index];
  const img = dialog.querySelector('[data-photo-image]');
  img.src = link.href;
  img.alt = link.getAttribute('aria-label') || 'Customer photo';
  dialog.querySelector('[data-photo-caption]').textContent = link.dataset.caption || '';
  dialog.querySelector('[data-photo-who]').textContent = link.dataset.who || '';
  dialog.querySelector('[data-photo-counter]').textContent = list.length > 1 ? `${photoViewer.index + 1} / ${list.length}` : '';
  dialog.querySelectorAll('[data-photo-prev], [data-photo-next]').forEach((b) => (b.hidden = list.length < 2));
}
document.addEventListener('click', (e) => {
  const dialog = document.getElementById('ReviewPhotoDialog');
  if (!dialog) return;
  const link = e.target.closest('a[data-review-photo]');
  if (link) {
    e.preventDefault();
    const scope = link.closest('[data-photo-group]') || document;
    const seen = new Set();
    photoViewer.list = [...scope.querySelectorAll('a[data-review-photo]')].filter((a) => {
      if (seen.has(a.href)) return false;
      seen.add(a.href);
      return true;
    });
    const start = photoViewer.list.findIndex((a) => a.href === link.href);
    showReviewPhoto(dialog, Math.max(0, start));
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('overflow-hidden');
    return;
  }
  if (!dialog.open) return;
  if (e.target.closest('[data-photo-prev]')) showReviewPhoto(dialog, photoViewer.index - 1);
  else if (e.target.closest('[data-photo-next]')) showReviewPhoto(dialog, photoViewer.index + 1);
  else if (e.target.closest('[data-photo-close]') || !e.target.closest('img, [data-photo-caption], [data-photo-who], button')) dialog.close();
});
document.addEventListener('keydown', (e) => {
  const dialog = document.getElementById('ReviewPhotoDialog');
  if (!dialog?.open) return;
  if (e.key === 'ArrowLeft') showReviewPhoto(dialog, photoViewer.index - 1);
  if (e.key === 'ArrowRight') showReviewPhoto(dialog, photoViewer.index + 1);
});
document.addEventListener('close', (e) => {
  if (e.target.id === 'ReviewPhotoDialog') document.documentElement.classList.remove('overflow-hidden');
}, true);

/* "/" focuses the header search from anywhere (unless the customer is typing in a field) */
document.addEventListener('keydown', (e) => {
  if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
  const target = e.target;
  if (target.closest?.('input, textarea, select, [contenteditable="true"], dialog[open]')) return;
  const input = document.querySelector('[data-search-shortcut]');
  if (!input || !input.offsetParent) return;
  e.preventDefault();
  input.focus();
});

/* ------------------------------------------------------------------ */
/* Review totals: every [data-review-total] (snippets/review-total)     */
/* shows Stamped's live store-wide count. Fetched once per visit and   */
/* cached for six hours; the figure in the site values shows until then.*/
/* ------------------------------------------------------------------ */
{
  const els = document.querySelectorAll('[data-review-total]');
  const cfg = TAT.settings?.stamped;
  const KEY = 'tat-review-total';
  const show = (n) =>
    document.querySelectorAll('[data-review-total]').forEach((el) => {
      el.textContent = el.dataset.format === 'exact' ? n.toLocaleString('en-AU') : `${(Math.floor(n / 50) * 50).toLocaleString('en-AU')}+`;
    });
  if (els.length && cfg?.key && cfg?.store) {
    let cached = null;
    try {
      cached = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    } catch {}
    if (cached?.n && Date.now() - cached.t < 6 * 3600e3) show(cached.n);
    else {
      const params = new URLSearchParams({ storeUrl: cfg.store, apiKey: cfg.key, take: '1', page: '1' });
      fetch(`https://stamped.io/api/widget/reviews?${params}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          const n = Number(data?.totalAll);
          if (!n) return;
          show(n);
          try {
            sessionStorage.setItem(KEY, JSON.stringify({ n, t: Date.now() }));
          } catch {}
        })
        .catch(() => {});
    }
  }
}


/* ------------------------------------------------------------------ */
/* Offers page filter chips (sections/offers-hub.liquid)                */
/* ------------------------------------------------------------------ */
document.querySelectorAll('[data-offer-filters]').forEach((bar) => {
  const grid = document.getElementById(bar.getAttribute('aria-controls'));
  if (!grid) return;
  bar.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-offer-filter]');
    if (!chip) return;
    const type = chip.dataset.offerFilter;
    bar.querySelectorAll('[data-offer-filter]').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
    grid.querySelectorAll('[data-offer-type]').forEach((card) => card.classList.toggle('hidden', !!type && card.dataset.offerType !== type));
    grid.toggleAttribute('data-filtered', !!type);
  });
});
